/**
 * GridMapGameView — Map-centric game view prototype.
 * The grid map fills the main area while game panels (chat, character, world)
 * live in a sidebar. Generator controls are in a header popover.
 * Supports AI-driven dungeon content generation for BSP dungeons.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ChevronDown, Grid3x3, Shuffle,
  ScrollText, Sparkles, Loader2,
} from 'lucide-react';
import { GridMapCanvas } from '@/components/game-ui/GridMapCanvas';
import { GridMapSidebar } from '@/components/game-ui/GridMapSidebar';
import { DialogueOverlay, type DialogueState } from '@/components/game-ui/DialogueOverlay';
import { EntityInspectCard } from '@/components/game-ui/EntityInspectCard';
import { processEvents, type CustomTriggerEvent } from '@/lib/game-logic/process-events';
import { cn } from '@/lib/utils';
import {
  applyDungeonContent,
  getRoomAtPosition,
  type DungeonContent,
  type RuntimeTriggerResponse,
} from '@/lib/grid-map-ai';
import {
  generateMap,
  createGridMapState,
  applyInput,
  coordKey,
  PRESET_LABELS,
  DEFAULT_RENDER_CONFIG,
  DEFAULT_GENERATOR_CONFIG,
  type GridMapState,
  type GeneratorConfig,
  type GeneratorType,
  type RenderConfig,
  type MoveDirection,
  type CityDistrictType,
  type DungeonTheme,
  type CityDistrictConfig,
  type RoomMetadata,
  type Entity,
} from '@omen-quest/grid-map';

// ============================================================================
// CONSTANTS
// ============================================================================

const GENERATOR_LABELS: Record<GeneratorType, string> = {
  bsp_dungeon: 'BSP Dungeon',
  cave: 'Cave',
  arena: 'Arena',
  city: 'City',
  preset: 'Preset',
};

const ALL_DISTRICT_TYPES: CityDistrictType[] = ['residential', 'market', 'noble', 'slums'];

const DUNGEON_THEMES: DungeonTheme[] = ['fortress', 'crypt', 'lair', 'ruins', 'sewer'];

const DISTRICT_LABELS: Record<CityDistrictType, string> = {
  residential: 'Residential',
  market: 'Market',
  noble: 'Noble',
  slums: 'Slums',
  temple: 'Temple',
  waterfront: 'Waterfront',
  craftsmen: 'Craftsmen',
};

const THEME_LABELS: Record<DungeonTheme, string> = {
  fortress: 'Fortress',
  crypt: 'Crypt',
  lair: 'Lair',
  ruins: 'Ruins',
  sewer: 'Sewer',
};

// ============================================================================
// TEST PRESETS
// ============================================================================

interface TestPreset {
  label: string;
  seed: number;
  theme: DungeonTheme;
  width: number;
  height: number;
  enableSecrets: boolean;
  enableKeys: boolean;
}

const TEST_PRESETS: TestPreset[] = [
  { label: 'Fortress Raid', seed: 42, theme: 'fortress', width: 50, height: 40, enableSecrets: true, enableKeys: true },
  { label: 'Crypt Exploration', seed: 1337, theme: 'crypt', width: 40, height: 30, enableSecrets: false, enableKeys: false },
  { label: 'Sewer Chase', seed: 999, theme: 'sewer', width: 35, height: 25, enableSecrets: false, enableKeys: false },
];

// ============================================================================
// PAGE COMPONENT
// ============================================================================

export function GridMapGameView() {
  // Map state
  const [genConfig, setGenConfig] = useState<GeneratorConfig>({
    ...DEFAULT_GENERATOR_CONFIG,
    type: 'city',
    seed: 42,
    width: 60,
    height: 45,
  });
  const [renderConfig, setRenderConfig] = useState<RenderConfig>({ ...DEFAULT_RENDER_CONFIG });
  const [state, setState] = useState<GridMapState | null>(null);

  // Dialogue overlay
  const [dialogue, setDialogue] = useState<DialogueState | null>(null);

  // Entity inspect card
  const [inspectedEntity, setInspectedEntity] = useState<Entity | null>(null);

  // Generator popover
  const [showGenPanel, setShowGenPanel] = useState(false);
  const genPanelRef = useRef<HTMLDivElement>(null);
  const genButtonRef = useRef<HTMLButtonElement>(null);

  // City-specific state
  const [selectedDistricts, setSelectedDistricts] = useState<Set<CityDistrictType>>(new Set());

  // Dungeon-specific state
  const [dungeonTheme, setDungeonTheme] = useState<DungeonTheme>('fortress');
  const [enableSecrets, setEnableSecrets] = useState(false);
  const [enableKeys, setEnableKeys] = useState(false);

  // AI dungeon content state
  const [dungeonContent, setDungeonContent] = useState<DungeonContent | null>(null);
  const [isPopulating, setIsPopulating] = useState(false);
  const [visitedRoomIds, setVisitedRoomIds] = useState<Set<number>>(new Set());
  const [currentRoom, setCurrentRoom] = useState<{ name: string; description: string } | null>(null);

  // Track the current generated map's metadata for AI calls
  const currentMapRef = useRef<{ name: string; theme: DungeonTheme; seed: number; rooms: RoomMetadata[] } | null>(null);

  // ============================================================================
  // ROOM TRACKING — update current room on player move
  // ============================================================================

  const updateRoomTracking = useCallback((mapState: GridMapState) => {
    if (!dungeonContent || !currentMapRef.current?.rooms) return;

    const player = mapState.map.entities.find(e => e.id === mapState.playerId);
    if (!player) return;

    const room = getRoomAtPosition(currentMapRef.current.rooms, player.x, player.y);
    if (room) {
      setVisitedRoomIds(prev => {
        if (prev.has(room.roomId)) return prev;
        const next = new Set(prev);
        next.add(room.roomId);
        return next;
      });

      const roomDef = dungeonContent.rooms.find(r => r.roomId === room.roomId);
      if (roomDef) {
        setCurrentRoom({ name: roomDef.name, description: roomDef.description });
      }
    }
  }, [dungeonContent]);

  // ============================================================================
  // RUNTIME TRIGGER HANDLER
  // ============================================================================

  const handleCustomTrigger = useCallback(async (events: CustomTriggerEvent[]) => {
    if (events.length === 0 || !dungeonContent) return;

    const event = events[0];
    setDialogue({ text: 'The dungeon stirs...', variant: 'loading' });

    try {
      const response = await fetch('/api/grid-map/trigger', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          triggerContext: JSON.stringify({ eventName: event.eventName, ...event.data }),
          roomName: currentRoom?.name ?? 'Unknown Chamber',
          roomDescription: currentRoom?.description ?? '',
          dungeonStory: `${dungeonContent.story.title}: ${dungeonContent.story.hook}`,
          visitedRooms: [...visitedRoomIds].map(id =>
            dungeonContent.rooms.find(r => r.roomId === id)?.name ?? `Room ${id}`
          ).join(', '),
        }),
      });

      if (!response.ok) throw new Error('Trigger API failed');

      const result: RuntimeTriggerResponse = await response.json();

      setDialogue({
        text: result.narrative,
        speaker: result.speaker ?? undefined,
        variant: 'dialogue',
      });

      // Apply state changes from runtime trigger
      if (result.stateChanges) {
        setState(prev => {
          if (!prev) return prev;
          let next = prev;
          if (result.stateChanges!.setsFlag) {
            const newFlags = new Set(next.triggerFlags);
            newFlags.add(result.stateChanges!.setsFlag!);
            next = { ...next, triggerFlags: newFlags };
          }
          return next;
        });
      }
    } catch {
      setDialogue({
        text: 'The darkness swallows your words...',
        speaker: 'Narrator',
        variant: 'dialogue',
      });
    }
  }, [dungeonContent, currentRoom, visitedRoomIds]);

  // ============================================================================
  // EVENT DRAIN — process pendingEvents after state changes
  // ============================================================================

  const drainEvents = useCallback((newState: GridMapState): GridMapState => {
    if (newState.pendingEvents.length > 0) {
      const { dialogue: dialogueState, customEvents } = processEvents(newState.pendingEvents);
      if (dialogueState) {
        setDialogue(dialogueState);
        setInspectedEntity(null);
      }
      if (customEvents.length > 0) {
        handleCustomTrigger(customEvents);
      }
    }
    return newState;
  }, [handleCustomTrigger]);

  /** Apply input and drain any resulting events. */
  const applyAndDrain = useCallback((prev: GridMapState, input: Parameters<typeof applyInput>[1]): GridMapState => {
    const next = applyInput(prev, input);
    updateRoomTracking(next);
    return drainEvents(next);
  }, [drainEvents, updateRoomTracking]);

  // ============================================================================
  // GENERATE
  // ============================================================================

  const generate = useCallback((configOverride?: GeneratorConfig) => {
    const config = configOverride ? { ...configOverride } : { ...genConfig };

    if (!configOverride) {
      if (config.type === 'city' && selectedDistricts.size > 0) {
        config.districts = [...selectedDistricts].map(type => ({ type } as CityDistrictConfig));
      }

      if (config.type === 'bsp_dungeon') {
        config.dungeonTheme = dungeonTheme;
        config.enableSecrets = enableSecrets;
        config.enableKeys = enableKeys;
      }
    }

    const map = generateMap(config);
    const newState = createGridMapState(map);
    // Drain initial events (e.g. triggers visible from spawn)
    drainEvents(newState);
    setState(newState);
    setDialogue(null);
    setInspectedEntity(null);
    // Reset AI content on new generation
    setDungeonContent(null);
    setVisitedRoomIds(new Set());
    setCurrentRoom(null);
    // Store metadata for AI populate
    if (config.type === 'bsp_dungeon' && map.metadata?.rooms) {
      currentMapRef.current = {
        name: map.name ?? 'Unknown Dungeon',
        theme: config.dungeonTheme ?? 'fortress',
        seed: config.seed,
        rooms: map.metadata.rooms,
      };
    } else {
      currentMapRef.current = null;
    }
  }, [genConfig, selectedDistricts, dungeonTheme, enableSecrets, enableKeys, drainEvents]);

  // Generate on mount
  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const randomize = useCallback(() => {
    const newSeed = Math.floor(Math.random() * 999999);
    setGenConfig(prev => ({ ...prev, seed: newSeed }));
  }, []);

  // ============================================================================
  // AI POPULATE
  // ============================================================================

  const populateDungeon = useCallback(async () => {
    if (!state || !currentMapRef.current || isPopulating || dungeonContent) return;

    setIsPopulating(true);
    try {
      const { name, theme, seed, rooms } = currentMapRef.current;
      const response = await fetch('/api/grid-map/populate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metadata: {
            rooms,
            dungeonZones: state.map.metadata?.dungeonZones,
          },
          dungeonTheme: theme,
          dungeonName: name,
          seed,
        }),
      });

      if (!response.ok) throw new Error('Populate API failed');

      const content: DungeonContent = await response.json();
      setDungeonContent(content);

      // Apply content to map
      const enrichedMap = applyDungeonContent(state.map, content, rooms);
      const newState = createGridMapState(enrichedMap);
      drainEvents(newState);
      setState(newState);

      // Track initial room
      const player = enrichedMap.entities.find(e => e.id === 'player');
      if (player) {
        const room = getRoomAtPosition(rooms, player.x, player.y);
        if (room) {
          setVisitedRoomIds(new Set([room.roomId]));
          const roomDef = content.rooms.find(r => r.roomId === room.roomId);
          if (roomDef) {
            setCurrentRoom({ name: roomDef.name, description: roomDef.description });
          }
        }
      }
    } catch (error) {
      console.error('Failed to populate dungeon:', error);
    } finally {
      setIsPopulating(false);
    }
  }, [state, isPopulating, dungeonContent, drainEvents]);

  // ============================================================================
  // TEST PRESET HANDLER
  // ============================================================================

  const applyTestPreset = useCallback((preset: TestPreset) => {
    const config: GeneratorConfig = {
      ...genConfig,
      type: 'bsp_dungeon',
      seed: preset.seed,
      width: preset.width,
      height: preset.height,
      dungeonTheme: preset.theme,
      enableSecrets: preset.enableSecrets,
      enableKeys: preset.enableKeys,
    };
    // Sync UI state for the generator panel
    setDungeonTheme(preset.theme);
    setEnableSecrets(preset.enableSecrets);
    setEnableKeys(preset.enableKeys);
    setGenConfig(config);
    // Generate immediately with the preset config (don't wait for state update)
    generate(config);
  }, [genConfig, generate]);

  // ============================================================================
  // KEYBOARD HANDLER
  // ============================================================================

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!state) return;
      // Don't capture if typing in an input/textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLSelectElement ||
        e.target instanceof HTMLTextAreaElement
      ) return;
      // Don't process movement if dialogue is showing
      if (dialogue) return;

      // ESC dismisses inspect card
      if (e.key === 'Escape' && inspectedEntity) {
        setInspectedEntity(null);
        return;
      }

      const dirMap: Record<string, MoveDirection> = {
        w: 'up', W: 'up', ArrowUp: 'up',
        s: 'down', S: 'down', ArrowDown: 'down',
        a: 'left', A: 'left', ArrowLeft: 'left',
        d: 'right', D: 'right', ArrowRight: 'right',
      };

      const dir = dirMap[e.key];
      if (dir) {
        e.preventDefault();
        setInspectedEntity(null);
        setState(prev => prev ? applyAndDrain(prev, { type: 'move', direction: dir }) : prev);
        return;
      }

      if (e.key === 'f' || e.key === 'F') {
        setRenderConfig(prev => ({ ...prev, fogEnabled: !prev.fogEnabled }));
      }
      if (e.key === 'g' || e.key === 'G') {
        setRenderConfig(prev => ({ ...prev, showGrid: !prev.showGrid }));
      }
      if (e.key === 't' || e.key === 'T') {
        setRenderConfig(prev => ({ ...prev, showTriggers: !prev.showTriggers }));
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state, dialogue, inspectedEntity, applyAndDrain]);

  // ============================================================================
  // CLICK-OUTSIDE HANDLER FOR POPOVER
  // ============================================================================

  useEffect(() => {
    if (!showGenPanel) return;

    function handleClickOutside(e: MouseEvent) {
      if (
        genPanelRef.current && !genPanelRef.current.contains(e.target as Node) &&
        genButtonRef.current && !genButtonRef.current.contains(e.target as Node)
      ) {
        setShowGenPanel(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGenPanel]);

  // ============================================================================
  // MAP INTERACTION HANDLERS
  // ============================================================================

  const handleCellClick = useCallback((x: number, y: number) => {
    if (!state || dialogue) return;
    setInspectedEntity(null);
    const player = state.map.entities.find(e => e.id === state.playerId);
    if (!player) return;
    const dx = x - player.x;
    const dy = y - player.y;
    const isAdjacent = Math.abs(dx) + Math.abs(dy) === 1;
    const isOnPlayer = dx === 0 && dy === 0;

    // Check for interact triggers on adjacent or same cell
    const triggers = state.map.triggers ?? [];
    const hasInteractTrigger = triggers.some(t =>
      t.condition === 'interact' &&
      (t.enabled !== false) &&
      (t.oneShot === false || !state.firedTriggers.has(t.id)) &&
      t.cells.some(c => c.x === x && c.y === y)
    );

    if ((isAdjacent || isOnPlayer) && hasInteractTrigger) {
      setState(prev => prev ? applyAndDrain(prev, { type: 'interact', x, y }) : prev);
      return;
    }

    // Check if clicking an adjacent closed door — open it without moving
    const cell = state.map.cells[y]?.[x];
    if (isAdjacent && cell?.terrain === 'door' && !state.openDoors.has(coordKey(x, y))) {
      setState(prev => prev ? applyAndDrain(prev, { type: 'toggle_door', x, y }) : prev);
      return;
    }

    // Entity inspection — click any entity on the clicked cell
    const entity = state.map.entities.find(e => e.x === x && e.y === y);
    if (entity) {
      // Player is always inspectable; others require visible fog
      const isVisible = entity.id === state.playerId || state.fogGrid[y]?.[x] === 'visible';
      if (isVisible) {
        setInspectedEntity(entity);
        return;
      }
    }

    let dir: MoveDirection | null = null;
    if (dx === 0 && dy === -1) dir = 'up';
    else if (dx === 0 && dy === 1) dir = 'down';
    else if (dx === -1 && dy === 0) dir = 'left';
    else if (dx === 1 && dy === 0) dir = 'right';
    if (dir) {
      setState(prev => prev ? applyAndDrain(prev, { type: 'move', direction: dir! }) : prev);
    }
  }, [state, dialogue, applyAndDrain]);

  const handleMoveRequest = useCallback((direction: MoveDirection) => {
    if (dialogue) return;
    setInspectedEntity(null);
    setState(prev => prev ? applyAndDrain(prev, { type: 'move', direction }) : prev);
  }, [dialogue, applyAndDrain]);

  const toggleDistrict = useCallback((type: CityDistrictType) => {
    setSelectedDistricts(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* ── Header ── */}
      <header className="relative z-20 flex items-center gap-3 border-b border-neutral-800 bg-neutral-950 px-4 py-2.5">
        {/* Back */}
        <Link
          to="/admin"
          className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </Link>

        <div className="w-px h-5 bg-white/10" />

        <h1 className="text-sm font-medium text-neutral-200">Grid Map Game View</h1>

        <div className="flex-1" />

        {/* Generator popover trigger */}
        <button
          ref={genButtonRef}
          onClick={() => setShowGenPanel(prev => !prev)}
          className={cn(
            'flex items-center gap-1.5 text-sm rounded px-3 py-1.5 transition-colors',
            showGenPanel
              ? 'bg-amber-500/20 text-amber-400'
              : 'bg-white/5 text-neutral-400 hover:text-white hover:bg-white/10',
          )}
        >
          <Grid3x3 className="w-4 h-4" />
          Generator
          <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showGenPanel && 'rotate-180')} />
        </button>

        {/* Test presets dropdown (BSP dungeon only) */}
        {genConfig.type === 'bsp_dungeon' && (
          <select
            className="text-sm rounded px-2 py-1.5 bg-white/5 text-neutral-400 border border-white/10 hover:bg-white/10 transition-colors"
            value=""
            onChange={e => {
              const preset = TEST_PRESETS[Number(e.target.value)];
              if (preset) {
                applyTestPreset(preset);
              }
            }}
          >
            <option value="" disabled>Test Presets</option>
            {TEST_PRESETS.map((preset, i) => (
              <option key={i} value={i}>{preset.label}</option>
            ))}
          </select>
        )}

        {/* AI Populate button (only when a BSP dungeon has been generated) */}
        {state && state.map.metadata?.rooms?.length && (
          <button
            onClick={populateDungeon}
            disabled={isPopulating || !!dungeonContent}
            className={cn(
              'flex items-center gap-1.5 text-sm rounded px-3 py-1.5 transition-colors',
              dungeonContent
                ? 'bg-emerald-500/10 text-emerald-500/60 cursor-not-allowed'
                : isPopulating
                  ? 'bg-purple-500/20 text-purple-400 cursor-wait'
                  : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30',
            )}
          >
            {isPopulating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {dungeonContent ? 'Populated' : isPopulating ? 'Populating...' : 'AI Populate'}
          </button>
        )}

        {/* Quests placeholder */}
        <button
          className="flex items-center gap-1.5 text-sm rounded px-3 py-1.5 bg-white/5 text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <ScrollText className="w-4 h-4" />
          Quests
        </button>
      </header>

      {/* ── Generator Popover ── */}
      {showGenPanel && (
        <div
          ref={genPanelRef}
          className="absolute z-30 top-[49px] right-4 w-[420px] bg-neutral-900 border border-neutral-700 rounded-lg shadow-2xl shadow-black/50 p-4"
        >
          <div className="space-y-3">
            {/* Type selector */}
            <div className="flex items-center gap-2">
              <label className="text-xs text-neutral-500 uppercase tracking-wide w-16">Type</label>
              <select
                className="flex-1 bg-neutral-800 text-white text-sm border border-white/10 rounded px-2 py-1.5"
                value={genConfig.type}
                onChange={e => {
                  const type = e.target.value as GeneratorType;
                  setGenConfig(prev => ({
                    ...prev,
                    type,
                    ...(type === 'city' && prev.width < 50 ? { width: 60, height: 45 } : {}),
                  }));
                }}
              >
                {Object.entries(GENERATOR_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* Preset selector */}
            {genConfig.type === 'preset' && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-neutral-500 uppercase tracking-wide w-16">Preset</label>
                <select
                  className="flex-1 bg-neutral-800 text-white text-sm border border-white/10 rounded px-2 py-1.5"
                  value={genConfig.presetName ?? 'pillar_hall'}
                  onChange={e => setGenConfig(prev => ({ ...prev, presetName: e.target.value }))}
                >
                  {Object.entries(PRESET_LABELS).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* City district chips */}
            {genConfig.type === 'city' && (
              <div className="flex items-start gap-2">
                <label className="text-xs text-neutral-500 uppercase tracking-wide w-16 pt-1">Districts</label>
                <div className="flex flex-wrap gap-1.5 flex-1">
                  {ALL_DISTRICT_TYPES.map(dt => (
                    <button
                      key={dt}
                      onClick={() => toggleDistrict(dt)}
                      className={cn(
                        'text-xs rounded px-2 py-0.5 transition-colors border',
                        selectedDistricts.has(dt)
                          ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                          : 'bg-white/5 text-neutral-500 border-white/10 hover:text-neutral-300',
                      )}
                    >
                      {DISTRICT_LABELS[dt]}
                    </button>
                  ))}
                  {selectedDistricts.size === 0 && (
                    <span className="text-xs text-neutral-600 italic">auto</span>
                  )}
                </div>
              </div>
            )}

            {/* Dungeon options */}
            {genConfig.type === 'bsp_dungeon' && (
              <>
                <div className="flex items-center gap-2">
                  <label className="text-xs text-neutral-500 uppercase tracking-wide w-16">Theme</label>
                  <select
                    className="flex-1 bg-neutral-800 text-white text-sm border border-white/10 rounded px-2 py-1.5"
                    value={dungeonTheme}
                    onChange={e => setDungeonTheme(e.target.value as DungeonTheme)}
                  >
                    {DUNGEON_THEMES.map(t => (
                      <option key={t} value={t}>{THEME_LABELS[t]}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 pl-[4.5rem]">
                  <button
                    onClick={() => setEnableSecrets(prev => !prev)}
                    className={cn(
                      'text-xs rounded px-2 py-0.5 transition-colors border',
                      enableSecrets
                        ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                        : 'bg-white/5 text-neutral-500 border-white/10 hover:text-neutral-300',
                    )}
                  >
                    Secrets
                  </button>
                  <button
                    onClick={() => setEnableKeys(prev => !prev)}
                    className={cn(
                      'text-xs rounded px-2 py-0.5 transition-colors border',
                      enableKeys
                        ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                        : 'bg-white/5 text-neutral-500 border-white/10 hover:text-neutral-300',
                    )}
                  >
                    Keys
                  </button>
                </div>
              </>
            )}

            {/* Size + seed (not for presets) */}
            {genConfig.type !== 'preset' && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-neutral-500 uppercase tracking-wide w-16">Size</label>
                <div className="flex items-center gap-1.5 flex-1">
                  <input
                    type="number"
                    className="bg-neutral-800 text-white text-sm border border-white/10 rounded px-2 py-1.5 w-16"
                    value={genConfig.width}
                    min={10}
                    max={100}
                    onChange={e => setGenConfig(prev => ({ ...prev, width: Number(e.target.value) }))}
                  />
                  <span className="text-neutral-600 text-xs">×</span>
                  <input
                    type="number"
                    className="bg-neutral-800 text-white text-sm border border-white/10 rounded px-2 py-1.5 w-16"
                    value={genConfig.height}
                    min={10}
                    max={100}
                    onChange={e => setGenConfig(prev => ({ ...prev, height: Number(e.target.value) }))}
                  />
                  <div className="w-px h-5 bg-white/10 mx-1" />
                  <label className="text-xs text-neutral-500 uppercase tracking-wide">Seed</label>
                  <input
                    type="number"
                    className="bg-neutral-800 text-white text-sm border border-white/10 rounded px-2 py-1.5 w-20"
                    value={genConfig.seed}
                    onChange={e => setGenConfig(prev => ({ ...prev, seed: Number(e.target.value) }))}
                  />
                  <button
                    onClick={randomize}
                    className="p-1.5 text-neutral-400 hover:text-white transition-colors"
                    title="Randomize seed"
                  >
                    <Shuffle className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Generate button */}
            <div className="flex justify-end pt-1">
              <button
                onClick={() => {
                  generate();
                  setShowGenPanel(false);
                }}
                className="flex items-center gap-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-sm font-medium rounded px-4 py-2 transition-colors"
              >
                <Grid3x3 className="w-4 h-4" />
                Generate Map
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main area ── */}
      <div className="flex flex-1 min-h-0">
        {/* Map canvas — takes remaining space */}
        <div className="relative flex-1 min-w-0 min-h-0">
          {state ? (
            <GridMapCanvas
              state={state}
              config={renderConfig}
              onCellClick={handleCellClick}
              onMoveRequest={handleMoveRequest}
              className="w-full h-full"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-neutral-600">
              Generating map...
            </div>
          )}

          {/* Entity inspect card */}
          {inspectedEntity && !dialogue && (
            <EntityInspectCard
              entity={inspectedEntity}
              onDismiss={() => setInspectedEntity(null)}
            />
          )}

          {/* Dialogue overlay */}
          {dialogue && (
            <DialogueOverlay
              text={dialogue.text}
              speaker={dialogue.speaker}
              variant={dialogue.variant}
              onDismiss={() => setDialogue(null)}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="w-[380px] shrink-0 hidden md:block">
          <GridMapSidebar
            renderConfig={renderConfig}
            onRenderConfigChange={setRenderConfig}
            dungeonContent={dungeonContent}
            currentRoom={currentRoom}
            visitedRoomIds={visitedRoomIds}
          />
        </div>
      </div>
    </div>
  );
}
